// API utility for reporting-central-ui
import { tokenService } from './tokenService';

async function getAccessToken() {
  return await tokenService.getAccessToken([`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`]);
}

export async function fetchDepartments(groupNames?: string[]): Promise<any> {
  const accessToken = await getAccessToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  
  // Add group names header if provided
  if (groupNames && groupNames.length > 0) {
    headers['X-User-Group-Names'] = JSON.stringify(groupNames);
  }
  
  const res = await fetch('/api/reports/filters/categories', {
    headers,
  });
  if (!res.ok) throw new Error('Failed to fetch categories');
  return res.json();
}

export async function fetchSubdepartments(category: string, groupNames?: string[]): Promise<any> {
  const accessToken = await getAccessToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  
  // Add group names header if provided
  if (groupNames && groupNames.length > 0) {
    headers['X-User-Group-Names'] = JSON.stringify(groupNames);
  }
  
  const res = await fetch(`/api/reports/filters/subcategories?category=${encodeURIComponent(category)}`, {
    headers,
  });
  if (!res.ok) throw new Error('Failed to fetch subcategories');
  return res.json();
}

export async function fetchReports(category: string, subcategory: string, groupNames?: string[]): Promise<any> {
  const accessToken = await getAccessToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  
  // Add group names header if provided
  if (groupNames && groupNames.length > 0) {
    headers['X-User-Group-Names'] = JSON.stringify(groupNames);
  }
  
  const params = new URLSearchParams();
  params.append('category', category);
  params.append('subcategory', subcategory);
  const res = await fetch(`/api/reports/filters/reports?${params.toString()}`, {
    headers,
  });
  if (!res.ok) throw new Error('Failed to fetch reports');
  return res.json();
}

export async function searchReports(query: string, groupNames?: string[]): Promise<any> {
  const accessToken = await getAccessToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  
  // Add group names header if provided
  if (groupNames && groupNames.length > 0) {
    headers['X-User-Group-Names'] = JSON.stringify(groupNames);
  }
  
  const params = new URLSearchParams();
  params.append('q', query);
  const res = await fetch(`/api/reports/search?${params.toString()}`, {
    headers,
  });
  if (!res.ok) throw new Error('Failed to search reports');
  return res.json();
}

export async function fetchPowerBIEmbedToken(reportid: string, workspaceid: string, type: string): Promise<any> {
  const accessToken = await getAccessToken();
  const res = await fetch(`/api/getEmbedToken?id=${reportid}&wid=${workspaceid}&userid=nouser&type=${type}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch embed token');
  return res.json();
}

export async function fetchProfilePhoto(accessToken: string): Promise<string | null> {
  try {
    const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (photoResponse.ok) {
      const blob = await photoResponse.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    return null;
  } catch (error) {
    return null;
  }
}

export async function fetchManagementReports(): Promise<any[]> {
  const accessToken = await getAccessToken();
  const res = await fetch('/api/reports', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to fetch management reports');
  return res.json();
}

export async function addManagementReport(report: any): Promise<any> {
  const accessToken = await getAccessToken();
  const res = await fetch('/api/reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(report),
  });
  if (!res.ok) throw new Error('Failed to add report');
  return res.json();
}

export async function deleteManagementReport(id: number): Promise<void> {
  const accessToken = await getAccessToken();
  const res = await fetch(`/api/reports/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error('Failed to delete report');
}

export async function updateManagementReport(id: number, report: any): Promise<any> {
  const accessToken = await getAccessToken();
  const res = await fetch(`/api/reports/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(report),
  });
  if (!res.ok) throw new Error('Failed to update report');
  return res.json();
} 